/**
 * Trunnion AI - Compare Diff View
 * Highlights differences between two executions with summary stats, step-by-step
 * comparison, expandable output/reasoning/tool-call sections, and token usage bars.
 */

import { useState, useMemo } from 'react';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clock,
  Cpu,
  Layers,
  ArrowDown,
  ArrowUp,
  Minus,
  Wrench,
  Brain,
  FileText,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { CodeBlock } from '@/components/ui/CodeBlock';
import { EXECUTION_STATUS_CONFIG } from '@/config/constants';
import type { Execution, ExecutionStep, ExecutionStatus } from '@/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Maps execution status to Badge variant. */
function statusVariant(
  status: ExecutionStatus,
): 'success' | 'error' | 'info' | 'warning' | 'default' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
    case 'timed_out':
      return 'error';
    case 'running':
      return 'info';
    case 'pending':
    case 'paused':
    case 'waiting_approval':
      return 'warning';
    default:
      return 'default';
  }
}

/** Compute duration in seconds between two ISO timestamps. Returns null when timestamps are missing. */
function durationSeconds(start?: string, end?: string): number | null {
  if (!start || !end) return null;
  return (new Date(end).getTime() - new Date(start).getTime()) / 1000;
}

/** Render a duration value as a human-readable string. */
function formatDuration(seconds: number | null): string {
  if (seconds === null) return '--';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toFixed(0)}s`;
}

/** Render a percentage change label with a directional icon. */
function DeltaIndicator({
  left,
  right,
  lowerIsBetter = true,
}: {
  left: number | null;
  right: number | null;
  lowerIsBetter?: boolean;
}) {
  if (left === null || right === null || left === 0) {
    return <span className="text-trunnion-slate-500">--</span>;
  }
  const pct = ((right - left) / left) * 100;
  if (Math.abs(pct) < 0.5) {
    return (
      <span className="inline-flex items-center gap-0.5 text-trunnion-slate-400 text-xs">
        <Minus className="h-3 w-3" />
        same
      </span>
    );
  }
  const improved = lowerIsBetter ? pct < 0 : pct > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium ${
        improved ? 'text-green-400' : 'text-red-400'
      }`}
    >
      {pct > 0 ? (
        <ArrowUp className="h-3 w-3" />
      ) : (
        <ArrowDown className="h-3 w-3" />
      )}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

/** Match steps between two executions by agentName (if available), then by index. */
function matchSteps(
  leftSteps: ExecutionStep[],
  rightSteps: ExecutionStep[],
): { left: ExecutionStep | null; right: ExecutionStep | null; label: string }[] {
  // Build lookup by agentName for the right side
  const rightByAgent = new Map<string, ExecutionStep>();
  const rightUsed = new Set<string>();

  for (const step of rightSteps) {
    const key = step.agentName ?? step.nodeName;
    if (key && !rightByAgent.has(key)) {
      rightByAgent.set(key, step);
    }
  }

  const matched: { left: ExecutionStep | null; right: ExecutionStep | null; label: string }[] = [];

  // First pass: match left steps to right by agentName/nodeName
  for (let i = 0; i < leftSteps.length; i++) {
    const leftStep = leftSteps[i];
    const key = leftStep.agentName ?? leftStep.nodeName;
    const rightStep = rightByAgent.get(key);

    if (rightStep && !rightUsed.has(rightStep.id)) {
      rightUsed.add(rightStep.id);
      matched.push({
        left: leftStep,
        right: rightStep,
        label: key || `Step ${i + 1}`,
      });
    } else {
      // Try index match
      const rightByIndex = rightSteps[i];
      if (rightByIndex && !rightUsed.has(rightByIndex.id)) {
        rightUsed.add(rightByIndex.id);
        matched.push({
          left: leftStep,
          right: rightByIndex,
          label: key || `Step ${i + 1}`,
        });
      } else {
        matched.push({
          left: leftStep,
          right: null,
          label: key || `Step ${i + 1}`,
        });
      }
    }
  }

  // Second pass: any unmatched right steps
  for (const rightStep of rightSteps) {
    if (!rightUsed.has(rightStep.id)) {
      matched.push({
        left: null,
        right: rightStep,
        label: rightStep.agentName ?? rightStep.nodeName ?? 'Unknown Step',
      });
    }
  }

  return matched;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CompareDiffViewProps {
  left: Execution;
  right: Execution;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function CompareDiffView({ left, right }: CompareDiffViewProps) {
  const leftDuration = durationSeconds(left.startedAt, left.completedAt);
  const rightDuration = durationSeconds(right.startedAt, right.completedAt);
  const pairedSteps = useMemo(() => matchSteps(left.steps, right.steps), [left, right]);

  return (
    <div className="space-y-6">
      {/* ----------------------------------------------------------------- */}
      {/* Summary Section                                                    */}
      {/* ----------------------------------------------------------------- */}
      <Card padding="none">
        <CardHeader>
          <CardTitle>Quick Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryMetric
              icon={<Layers className="h-4 w-4 text-blue-400" />}
              label="Status"
              leftValue={
                <Badge variant={statusVariant(left.status)} dot>
                  {EXECUTION_STATUS_CONFIG[left.status]?.label ?? left.status}
                </Badge>
              }
              rightValue={
                <Badge variant={statusVariant(right.status)} dot>
                  {EXECUTION_STATUS_CONFIG[right.status]?.label ?? right.status}
                </Badge>
              }
            />
            <SummaryMetric
              icon={<Clock className="h-4 w-4 text-trunnion-gold-500" />}
              label="Duration"
              leftValue={
                <span className="text-sm font-medium text-white">
                  {formatDuration(leftDuration)}
                </span>
              }
              rightValue={
                <span className="text-sm font-medium text-white">
                  {formatDuration(rightDuration)}
                </span>
              }
              delta={<DeltaIndicator left={leftDuration} right={rightDuration} lowerIsBetter />}
            />
            <SummaryMetric
              icon={<Cpu className="h-4 w-4 text-purple-400" />}
              label="Total Tokens"
              leftValue={
                <span className="text-sm font-medium text-white">
                  {left.totalTokensUsed.toLocaleString()}
                </span>
              }
              rightValue={
                <span className="text-sm font-medium text-white">
                  {right.totalTokensUsed.toLocaleString()}
                </span>
              }
              delta={
                <DeltaIndicator
                  left={left.totalTokensUsed}
                  right={right.totalTokensUsed}
                  lowerIsBetter
                />
              }
            />
            <SummaryMetric
              icon={<Layers className="h-4 w-4 text-cyan-400" />}
              label="Steps"
              leftValue={
                <span className="text-sm font-medium text-white">
                  {left.steps.length}
                </span>
              }
              rightValue={
                <span className="text-sm font-medium text-white">
                  {right.steps.length}
                </span>
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Step-by-Step Comparison                                            */}
      {/* ----------------------------------------------------------------- */}
      <Card padding="none">
        <CardHeader>
          <CardTitle>Step-by-Step Comparison</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {pairedSteps.length === 0 ? (
            <p className="text-sm text-trunnion-slate-400 text-center py-4">
              No steps to compare.
            </p>
          ) : (
            pairedSteps.map((pair, idx) => (
              <StepPairRow
                key={`${pair.left?.id ?? 'none'}-${pair.right?.id ?? 'none'}-${idx}`}
                label={pair.label}
                leftStep={pair.left}
                rightStep={pair.right}
                index={idx}
              />
            ))
          )}
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Token Usage Comparison                                             */}
      {/* ----------------------------------------------------------------- */}
      <Card padding="none">
        <CardHeader>
          <CardTitle>Token Usage Per Step</CardTitle>
        </CardHeader>
        <CardContent>
          <TokenComparisonChart pairs={pairedSteps} />
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-Components
// ---------------------------------------------------------------------------

/** A single metric cell in the summary grid. */
function SummaryMetric({
  icon,
  label,
  leftValue,
  rightValue,
  delta,
}: {
  icon: React.ReactNode;
  label: string;
  leftValue: React.ReactNode;
  rightValue: React.ReactNode;
  delta?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-trunnion-slate-700/50 bg-trunnion-slate-800/30 p-3">
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <span className="text-xs font-medium text-trunnion-slate-400">{label}</span>
        {delta && <span className="ml-auto">{delta}</span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="block text-[10px] font-medium text-trunnion-slate-500 uppercase tracking-wider mb-0.5">
            Left
          </span>
          {leftValue}
        </div>
        <div>
          <span className="block text-[10px] font-medium text-trunnion-slate-500 uppercase tracking-wider mb-0.5">
            Right
          </span>
          {rightValue}
        </div>
      </div>
    </div>
  );
}

/** Row comparing a single step pair. */
function StepPairRow({
  label,
  leftStep,
  rightStep,
  index,
}: {
  label: string;
  leftStep: ExecutionStep | null;
  rightStep: ExecutionStep | null;
  index: number;
}) {
  const [expanded, setExpanded] = useState(false);

  const diffIcon = useMemo(() => {
    if (!leftStep || !rightStep) {
      return <AlertTriangle className="h-4 w-4 text-yellow-400" />;
    }
    if (leftStep.status === 'completed' && rightStep.status === 'completed') {
      return <CheckCircle2 className="h-4 w-4 text-green-400" />;
    }
    if (leftStep.status === 'failed' || rightStep.status === 'failed') {
      return <XCircle className="h-4 w-4 text-red-400" />;
    }
    // Different outputs or statuses
    return <AlertTriangle className="h-4 w-4 text-yellow-400" />;
  }, [leftStep, rightStep]);

  const leftDur = leftStep ? durationSeconds(leftStep.startedAt, leftStep.completedAt) : null;
  const rightDur = rightStep ? durationSeconds(rightStep.startedAt, rightStep.completedAt) : null;

  return (
    <div className="rounded-lg border border-trunnion-slate-700/50 bg-trunnion-slate-800/20">
      {/* Collapsed header */}
      <button
        onClick={() => setExpanded((p) => !p)}
        className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-trunnion-slate-800/40 transition-colors rounded-lg"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-trunnion-slate-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-trunnion-slate-400 shrink-0" />
        )}
        {diffIcon}
        <span className="text-sm font-medium text-white flex-1">
          {index + 1}. {label}
        </span>

        {/* Mini stats */}
        <div className="flex items-center gap-4 text-xs text-trunnion-slate-400">
          {/* Left status */}
          {leftStep ? (
            <Badge variant={statusVariant(leftStep.status)} className="text-[10px]">
              {EXECUTION_STATUS_CONFIG[leftStep.status]?.label ?? leftStep.status}
            </Badge>
          ) : (
            <span className="text-trunnion-slate-500 italic">missing</span>
          )}

          <span className="text-trunnion-slate-600">vs</span>

          {/* Right status */}
          {rightStep ? (
            <Badge variant={statusVariant(rightStep.status)} className="text-[10px]">
              {EXECUTION_STATUS_CONFIG[rightStep.status]?.label ?? rightStep.status}
            </Badge>
          ) : (
            <span className="text-trunnion-slate-500 italic">missing</span>
          )}

          {/* Duration comparison */}
          <span className="hidden sm:inline text-trunnion-slate-500">
            {formatDuration(leftDur)} / {formatDuration(rightDur)}
          </span>
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-trunnion-slate-700/50 px-4 py-4 space-y-4">
          {/* Metric row */}
          <div className="grid grid-cols-3 gap-3 text-xs">
            <MetricCell
              label="Duration"
              leftVal={formatDuration(leftDur)}
              rightVal={formatDuration(rightDur)}
              delta={<DeltaIndicator left={leftDur} right={rightDur} lowerIsBetter />}
            />
            <MetricCell
              label="Tokens"
              leftVal={leftStep?.tokenUsage?.totalTokens?.toLocaleString() ?? '--'}
              rightVal={rightStep?.tokenUsage?.totalTokens?.toLocaleString() ?? '--'}
              delta={
                <DeltaIndicator
                  left={leftStep?.tokenUsage?.totalTokens ?? null}
                  right={rightStep?.tokenUsage?.totalTokens ?? null}
                  lowerIsBetter
                />
              }
            />
            <MetricCell
              label="Tool Calls"
              leftVal={String(leftStep?.toolCalls?.length ?? 0)}
              rightVal={String(rightStep?.toolCalls?.length ?? 0)}
            />
          </div>

          {/* Output comparison */}
          <ExpandableSection
            icon={<FileText className="h-3.5 w-3.5" />}
            title="Output Comparison"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <CodeBlock
                title="Left Output"
                content={
                  leftStep?.output
                    ? JSON.stringify(leftStep.output, null, 2)
                    : 'No output'
                }
                language="json"
                maxHeight="250px"
              />
              <CodeBlock
                title="Right Output"
                content={
                  rightStep?.output
                    ? JSON.stringify(rightStep.output, null, 2)
                    : 'No output'
                }
                language="json"
                maxHeight="250px"
              />
            </div>
          </ExpandableSection>

          {/* Tool call comparison */}
          <ExpandableSection
            icon={<Wrench className="h-3.5 w-3.5" />}
            title="Tool Call Comparison"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <span className="text-[10px] font-semibold text-trunnion-slate-500 uppercase tracking-wider">
                  Left ({leftStep?.toolCalls?.length ?? 0} calls)
                </span>
                {(leftStep?.toolCalls ?? []).length === 0 ? (
                  <p className="text-xs text-trunnion-slate-500 italic">No tool calls</p>
                ) : (
                  leftStep!.toolCalls.map((tc) => (
                    <div
                      key={tc.id}
                      className="rounded border border-trunnion-slate-700/50 bg-trunnion-slate-900/50 p-2 text-xs"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-trunnion-slate-200">{tc.toolName}</span>
                        <Badge
                          variant={tc.status === 'success' ? 'success' : tc.status === 'failed' ? 'error' : 'default'}
                          className="text-[10px]"
                        >
                          {tc.status}
                        </Badge>
                      </div>
                      <pre className="text-[10px] text-trunnion-slate-400 overflow-auto max-h-24 font-mono">
                        {JSON.stringify(tc.parameters, null, 2)}
                      </pre>
                    </div>
                  ))
                )}
              </div>
              <div className="space-y-2">
                <span className="text-[10px] font-semibold text-trunnion-slate-500 uppercase tracking-wider">
                  Right ({rightStep?.toolCalls?.length ?? 0} calls)
                </span>
                {(rightStep?.toolCalls ?? []).length === 0 ? (
                  <p className="text-xs text-trunnion-slate-500 italic">No tool calls</p>
                ) : (
                  rightStep!.toolCalls.map((tc) => (
                    <div
                      key={tc.id}
                      className="rounded border border-trunnion-slate-700/50 bg-trunnion-slate-900/50 p-2 text-xs"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-trunnion-slate-200">{tc.toolName}</span>
                        <Badge
                          variant={tc.status === 'success' ? 'success' : tc.status === 'failed' ? 'error' : 'default'}
                          className="text-[10px]"
                        >
                          {tc.status}
                        </Badge>
                      </div>
                      <pre className="text-[10px] text-trunnion-slate-400 overflow-auto max-h-24 font-mono">
                        {JSON.stringify(tc.parameters, null, 2)}
                      </pre>
                    </div>
                  ))
                )}
              </div>
            </div>
          </ExpandableSection>

          {/* Reasoning comparison */}
          <ExpandableSection
            icon={<Brain className="h-3.5 w-3.5" />}
            title="Reasoning Comparison"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <span className="text-[10px] font-semibold text-trunnion-slate-500 uppercase tracking-wider">
                  Left ({leftStep?.reasoningTrace?.length ?? 0} entries)
                </span>
                {(leftStep?.reasoningTrace ?? []).length === 0 ? (
                  <p className="text-xs text-trunnion-slate-500 italic">No reasoning trace</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
                    {leftStep!.reasoningTrace.map((entry) => (
                      <div
                        key={entry.id}
                        className="rounded bg-trunnion-slate-900/50 p-2 text-xs"
                      >
                        <span className="text-[10px] font-semibold text-trunnion-slate-500 uppercase">
                          {entry.type}
                        </span>
                        <p className="text-trunnion-slate-300 whitespace-pre-wrap mt-0.5">
                          {entry.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <span className="text-[10px] font-semibold text-trunnion-slate-500 uppercase tracking-wider">
                  Right ({rightStep?.reasoningTrace?.length ?? 0} entries)
                </span>
                {(rightStep?.reasoningTrace ?? []).length === 0 ? (
                  <p className="text-xs text-trunnion-slate-500 italic">No reasoning trace</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
                    {rightStep!.reasoningTrace.map((entry) => (
                      <div
                        key={entry.id}
                        className="rounded bg-trunnion-slate-900/50 p-2 text-xs"
                      >
                        <span className="text-[10px] font-semibold text-trunnion-slate-500 uppercase">
                          {entry.type}
                        </span>
                        <p className="text-trunnion-slate-300 whitespace-pre-wrap mt-0.5">
                          {entry.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ExpandableSection>
        </div>
      )}
    </div>
  );
}

/** Compact metric cell used in the expanded step row. */
function MetricCell({
  label,
  leftVal,
  rightVal,
  delta,
}: {
  label: string;
  leftVal: string;
  rightVal: string;
  delta?: React.ReactNode;
}) {
  return (
    <div className="rounded border border-trunnion-slate-700/30 bg-trunnion-slate-900/30 p-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-medium text-trunnion-slate-500 uppercase tracking-wider">
          {label}
        </span>
        {delta}
      </div>
      <div className="grid grid-cols-2 gap-1">
        <span className="text-xs text-trunnion-slate-200">{leftVal}</span>
        <span className="text-xs text-trunnion-slate-200">{rightVal}</span>
      </div>
    </div>
  );
}

/** Collapsible section wrapper for detailed comparisons. */
function ExpandableSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-trunnion-slate-700/30">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-trunnion-slate-800/30 transition-colors rounded-lg"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-trunnion-slate-400" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-trunnion-slate-400" />
        )}
        <span className="text-trunnion-slate-400">{icon}</span>
        <span className="text-xs font-medium text-trunnion-slate-300">{title}</span>
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Token Usage Comparison Chart (pure CSS bars)
// ---------------------------------------------------------------------------

/** Simple horizontal bar chart comparing token usage per matched step pair. */
function TokenComparisonChart({
  pairs,
}: {
  pairs: { left: ExecutionStep | null; right: ExecutionStep | null; label: string }[];
}) {
  const maxTokens = useMemo(() => {
    let max = 1;
    for (const pair of pairs) {
      const leftTotal = pair.left?.tokenUsage?.totalTokens ?? 0;
      const rightTotal = pair.right?.tokenUsage?.totalTokens ?? 0;
      if (leftTotal > max) max = leftTotal;
      if (rightTotal > max) max = rightTotal;
    }
    return max;
  }, [pairs]);

  const stepsWithTokens = pairs.filter(
    (p) =>
      (p.left?.tokenUsage?.totalTokens ?? 0) > 0 ||
      (p.right?.tokenUsage?.totalTokens ?? 0) > 0,
  );

  if (stepsWithTokens.length === 0) {
    return (
      <p className="text-sm text-trunnion-slate-400 text-center py-4">
        No token usage data available for comparison.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex items-center gap-4 justify-end">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-blue-500" />
          <span className="text-xs text-trunnion-slate-400">Left</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-trunnion-gold-500" />
          <span className="text-xs text-trunnion-slate-400">Right</span>
        </div>
      </div>

      {stepsWithTokens.map((pair, idx) => {
        const leftTokens = pair.left?.tokenUsage?.totalTokens ?? 0;
        const rightTokens = pair.right?.tokenUsage?.totalTokens ?? 0;
        const leftPct = (leftTokens / maxTokens) * 100;
        const rightPct = (rightTokens / maxTokens) * 100;

        return (
          <div key={idx}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-trunnion-slate-300 truncate max-w-[50%]">
                {pair.label}
              </span>
              <DeltaIndicator left={leftTokens} right={rightTokens} lowerIsBetter />
            </div>
            {/* Left bar */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] text-trunnion-slate-500 w-8 text-right shrink-0">L</span>
              <div className="flex-1 h-4 bg-trunnion-slate-900 rounded overflow-hidden">
                <div
                  className="h-full bg-blue-500/70 rounded transition-all duration-500"
                  style={{ width: `${leftPct}%` }}
                />
              </div>
              <span className="text-[10px] text-trunnion-slate-400 w-16 text-right tabular-nums shrink-0">
                {leftTokens.toLocaleString()}
              </span>
            </div>
            {/* Right bar */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-trunnion-slate-500 w-8 text-right shrink-0">R</span>
              <div className="flex-1 h-4 bg-trunnion-slate-900 rounded overflow-hidden">
                <div
                  className="h-full bg-trunnion-gold-500/70 rounded transition-all duration-500"
                  style={{ width: `${rightPct}%` }}
                />
              </div>
              <span className="text-[10px] text-trunnion-slate-400 w-16 text-right tabular-nums shrink-0">
                {rightTokens.toLocaleString()}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
